import React, { useState, useEffect } from "react";
import { Table, Button, Space, message, Popconfirm, Card, Input, Select, Row, Col } from "antd";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import api from "../../api";
import { DeleteOutlined, EyeOutlined, PlusOutlined, FilterOutlined } from "@ant-design/icons";
import { debounce } from "lodash";

const OrdersIndex = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Get initial values from URL
  const initialSearch = searchParams.get('search') || "";
  const initialPage = parseInt(searchParams.get('page')) || 1;
  const initialPaymentMethod = searchParams.get('payment_method') || null;
  const initialPaymentGate = searchParams.get('payment_gate') || null;
  const initialPaymentStatus = searchParams.get('payment_status') || null;
  const initialDeliverStatus = searchParams.get('deliver_status') || null;

  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [filters, setFilters] = useState({
    payment_method: initialPaymentMethod,
    payment_gate: initialPaymentGate,
    payment_status: initialPaymentStatus,
    deliver_status: initialDeliverStatus
  });

  const StatusTag = ({ status, type }) => {
    const statusColors = {
      payment: {
        pending: { color: '#faad14', text: 'در انتظار پرداخت' },
        paid: { color: '#52c41a', text: 'پرداخت شده' },
        failed: { color: '#f5222d', text: 'پرداخت ناموفق' },
        refunded: { color: '#722ed1', text: 'عودت داده شده' },
        canceled: { color: '#d9d9d9', text: 'لغو شده' }
      },
      delivery: {
        pending: { color: '#faad14', text: 'در انتظار پردازش' },
        processing: { color: '#1890ff', text: 'در حال پردازش' },
        shipped: { color: '#13c2c2', text: 'ارسال شده' },
        delivered: { color: '#52c41a', text: 'تحویل داده شده' },
        canceled: { color: '#d9d9d9', text: 'لغو شده' },
        returned: { color: '#722ed1', text: 'مرجوع شده' }
      }
    };
  
    const config = statusColors[type][status] || { color: '#d9d9d9', text: 'نامشخص' };
  
    return (
      <span 
        style={{
          backgroundColor: config.color,
          color: '#fff',
          padding: '2px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          display: 'inline-block'
        }}
      >
        {config.text}
      </span>
    );
  };

  // Payment gate options
  const paymentGateOptions = [
    { value: 'zarinpal', label: 'زرین پال' },
    { value: 'asanpardakht', label: 'آسان پرداخت' }
  ];

  // Payment status options with Persian labels
  const paymentStatusOptions = [
    { value: 'pending', label: 'در انتظار پرداخت' },
    { value: 'paid', label: 'پرداخت شده' },
    { value: 'failed', label: 'پرداخت ناموفق' },
    { value: 'refunded', label: 'عودت داده شده' },
    { value: 'canceled', label: 'لغو شده' }
  ];

  // Delivery status options with Persian labels
  const deliverStatusOptions = [
    { value: 'pending', label: 'در انتظار پردازش' },
    { value: 'processing', label: 'در حال پردازش' },
    { value: 'shipped', label: 'ارسال شده' },
    { value: 'delivered', label: 'تحویل داده شده' },
    { value: 'canceled', label: 'لغو شده' },
    { value: 'returned', label: 'مرجوع شده' }
  ];

  // Payment method options
  const paymentMethodOptions = [
    { value: 'online', label: 'آنلاین' },
    { value: 'on_site', label: 'در محل' }
  ];

  const updateURLParams = (newParams) => {
    const params = new URLSearchParams(searchParams);
    
    // Update all parameters
    Object.entries(newParams).forEach(([key, value]) => {
      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
    });
    
    setSearchParams(params);
  };

  const buildQueryParams = () => {
    const params = new URLSearchParams();
    
    // Add pagination
    params.append('includes[]', 'products');
    params.append('page', pagination.current);
    params.append('per_page', pagination.pageSize);
    
    // Add search if exists
    if (searchTerm) {
      params.append('search', searchTerm);
    }
    
    // Add filters if they exist
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        params.append(key, value);
      }
    });
    
    return params.toString();
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const queryParams = buildQueryParams();
      const response = await api.get(`/panel/order?${queryParams}`);
      setOrders(response.data.data);
      setPagination({
        current: response.data.meta.current_page,
        pageSize: response.data.meta.per_page, 
        total: response.data.meta.total,
      });
    } catch (error) {
      message.error("خطا در دریافت سفارشات");
      console.error("خطا در دریافت سفارشات:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Set initial pagination from URL
    setPagination(prev => ({ ...prev, current: initialPage }));
    fetchOrders();
  }, [location.search]); // Trigger when URL changes

  const handleSearch = debounce((value) => {
    setSearchTerm(value);
    updateURLParams({ search: value, page: 1 });
  }, 300);

  const handleFilterChange = (name, value) => {
    const newFilters = { ...filters, [name]: value };
    setFilters(newFilters);
    updateURLParams({ ...newFilters, page: 1 });
  };

  const handlePaginationChange = (page) => {
    setPagination(prev => ({ ...prev, current: page }));
    updateURLParams({ page });
  };

  const resetFilters = () => {
    setFilters({
      payment_method: null,
      payment_gate: null,
      payment_status: null,
      deliver_status: null
    });
    setSearchTerm("");
    updateURLParams({
      payment_method: null,
      payment_gate: null,
      payment_status: null,
      deliver_status: null,
      search: null,
      page: 1
    });
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/panel/order/${id}`);
      message.success("سفارش با موفقیت حذف شد.");
      fetchOrders();
    } catch (error) {
      message.error("خطا در حذف سفارش");
      console.error("خطا در حذف سفارش:", error);
    }
  };

  const columns = [
    {
      title: "َشناسه",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "شماره سفارش",
      dataIndex: "property",
      key: "property",
    },
    {
      title: "کاربر",
      dataIndex: "user",
      key: "user",
      render: (user) => (
        <>
          <p><strong>نام:</strong> {user.name}</p>
          <p><strong>شماره:</strong> {user.number}</p>
          <p><strong>ایمیل:</strong> {user.email}</p>
        </>
      ),
    },
    {
      title: "وضعیت پرداخت",
      dataIndex: "payment_status",
      key: "payment_status",
      render: (status) => <StatusTag status={status} type="payment" />,
    },
    {
      title: "وضعیت تحویل",
      dataIndex: "deliver_status",
      key: "deliver_status",
      render: (status) => status ? 
        <StatusTag status={status} type="delivery" /> : 
        <span>-</span>,
    },
    {
      title: "قیمت",
      dataIndex: "price",
      key: "price",
      render: (price) => `${price.toLocaleString()} تومان`,
    },
    {
      title: "تاریخ ایجاد",
      dataIndex: "created_at",
      key: "created_at",
    },
    {
      title: "عملیات",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button type="primary" icon={<EyeOutlined />} onClick={() => navigate(`/orders/${record.id}`)}>
            جزئیات
          </Button>
          <Popconfirm
            title="آیا از حذف این سفارش مطمئن هستید؟"
            onConfirm={() => handleDelete(record.id)}
            okText="بله"
            cancelText="خیر"
          >
            <Button danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card title="سفارشات">
      <div className="mb-4">
        <Row gutter={16} className="mb-4">
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="جستجو..."
              onChange={(e) => handleSearch(e.target.value)}
              value={searchTerm}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="درگاه پرداخت"
              style={{ width: '100%' }}
              options={paymentGateOptions}
              value={filters.payment_gate}
              onChange={(value) => handleFilterChange('payment_gate', value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="وضعیت پرداخت"
              style={{ width: '100%' }}
              options={paymentStatusOptions}
              value={filters.payment_status}
              onChange={(value) => handleFilterChange('payment_status', value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="وضعیت تحویل"
              style={{ width: '100%' }}
              options={deliverStatusOptions}
              value={filters.deliver_status}
              onChange={(value) => handleFilterChange('deliver_status', value)}
              allowClear
            />
          </Col>
        </Row>
        <Row gutter={16} className="mb-4">
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="روش پرداخت"
              style={{ width: '100%' }}
              options={paymentMethodOptions}
              value={filters.payment_method}
              onChange={(value) => handleFilterChange('payment_method', value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Button
              icon={<FilterOutlined />}
              onClick={resetFilters}
            >
              حذف فیلترها
            </Button>
          </Col>
          <Col xs={24} sm={12} md={6} offset={6}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate("/orders/add")}
              style={{ float: 'left' }}
            >
              افزودن سفارش جدید
            </Button>
          </Col>
        </Row>
      </div>
      <Table
        columns={columns}
        dataSource={orders}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          onChange: handlePaginationChange,
        }}
        rowKey="id"
      />
    </Card>
  );
};

export default OrdersIndex;